import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Loader2, TrendingUp, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RANGES = [{ d: 7, l: '7d' }, { d: 30, l: '30d' }, { d: 90, l: '90d' }, { d: 0, l: 'All' }];
const SUBTABS = [
  { key: 'transition', label: 'Transition' },
  { key: 'employers', label: 'Employers' },
  { key: 'regions', label: 'Regions' },
  { key: 'opportunities', label: 'Opportunities' },
];

const Metric = ({ label, value, sub, tip }) => (
  <div className="p-3 rounded-lg border border-slate-200 bg-white">
    <p className="text-xs text-slate-500 flex items-center gap-1">
      {label}{tip && <span title={tip}><Info className="w-3 h-3 text-slate-300" /></span>}
    </p>
    <p className="text-2xl font-bold text-slate-900">{value ?? '—'}</p>
    {sub && <p className="text-xs text-slate-400">{sub}</p>}
  </div>
);

const Bar = ({ label, value, max }) => (
  <div className="mb-2">
    <div className="flex justify-between text-xs text-slate-600 mb-0.5"><span>{label}</span><span className="font-semibold">{value}</span></div>
    <div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-brand" style={{ width: `${max ? Math.round((value / max) * 100) : 0}%` }} /></div>
  </div>
);

export default function AdminAnalytics() {
  const [sub, setSub] = useState('transition');
  const [days, setDays] = useState(30);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const ep = (sub === 'employers') ? 'analytics/employers'
      : (sub === 'regions') ? 'analytics/regions'
      : (sub === 'opportunities') ? `analytics/opportunities?days=${days}`
      : `analytics/transition?days=${days}`;
    axios.get(`${BACKEND_URL}/api/admin/${ep}`, { withCredentials: true })
      .then(r => { if (active) setData(d => ({ ...d, [sub]: r.data })); })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [sub, days]);

  const d = data[sub];
  const eventPeriod = (sub === 'transition' || sub === 'opportunities');

  return (
    <div className="space-y-4" data-testid="admin-analytics-v2">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {SUBTABS.map(s => (
            <button key={s.key} data-testid={`analytics-sub-${s.key}`} onClick={() => setSub(s.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${sub === s.key ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100 border border-slate-200'}`}>{s.label}</button>
          ))}
        </div>
        {eventPeriod && (
          <div className="flex gap-1 ml-auto" data-testid="analytics-date-range">
            {RANGES.map(r => (
              <button key={r.l} onClick={() => setDays(r.d)} className={`px-2 py-1 rounded text-xs ${days === r.d ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{r.l}</button>
            ))}
          </div>
        )}
      </div>

      {loading && !d ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div> : !d ? (
        <Card className="p-8 text-center text-slate-500">No data.</Card>
      ) : (
        <>
          {sub === 'transition' && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-2">Current state <span className="text-xs font-normal text-slate-400">(full history)</span></h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Metric label="Total Job Seekers" value={d.current_state?.total_job_seekers} />
                  <Metric label="Active Job Seekers" value={d.current_state?.active_job_seekers} />
                  <Metric label="Resume Complete" value={`${d.current_state?.resume_complete_pct ?? 0}%`} sub={`${d.current_state?.resume_complete} of ${d.current_state?.resume_total}`} tip="Job seekers with a resume currently marked complete." />
                  <Metric label="Resume Visible" value={d.current_state?.resume_visible} />
                  <Metric label="With Saved" value={d.current_state?.seekers_with_saved} />
                  <Metric label="With Applications" value={d.current_state?.seekers_with_applications} />
                  <Metric label="In Motion" value={d.current_state?.applications_in_motion} tip="Applications whose status is not hired, rejected, withdrawn, or archived." />
                  <Metric label="Hires" value={d.current_state?.hires} tip="job_applications where hired = true." />
                  <Metric label="SkillBridge Apps" value={d.current_state?.skillbridge_applications} />
                  <Metric label="Employment Apps" value={d.current_state?.employment_applications} />
                  <Metric label="Pursuing Both" value={d.current_state?.seekers_pursuing_both} />
                  <Metric label="Time→1st App (median)" value={d.elapsed_time_days?.created_to_first_application_median} sub={`n=${d.elapsed_time_days?.sample_size ?? 0} days`} />
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-2">Transition funnel</h4>
                <Card className="p-4">
                  {(d.funnel || []).map((f, i, arr) => (
                    <Bar key={f.stage} label={`${f.stage}${f.source === 'since_tracking_began' ? ' (since tracking began)' : ''}`} value={f.value} max={arr[0]?.value || 1} />
                  ))}
                  <p className="text-xs text-slate-400 mt-2">Stages mix current-state and event-period data (labeled) — not a single-period conversion rate.</p>
                </Card>
              </div>
              <p className="text-xs text-slate-400">Opportunity Engaged (since tracking began, {d.since_tracking_began?.window_days} window): <strong>{d.since_tracking_began?.opportunity_engaged_seekers}</strong> unique seekers.</p>
            </div>
          )}

          {sub === 'employers' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Employer Accounts" value={d.employer_accounts?.total} sub={`${d.employer_accounts?.approved} approved`} tip="Actual Sergeant TAP employer users." />
              <Metric label="Jobs Published" value={d.employer_accounts?.jobs_published} sub={`${d.employer_accounts?.jobs_active} active`} />
              <Metric label="Applications Received" value={d.employer_accounts?.applications_received} />
              <Metric label="Hires" value={d.employer_accounts?.hires} sub={`${d.employer_accounts?.application_to_hire_pct}% of apps`} />
              <div className="col-span-2 md:col-span-4">
                <Card className="p-3 bg-slate-50 border-slate-200"><p className="text-xs text-slate-500"><strong>{d.listing_organizations?.total}</strong> listing organizations — {d.listing_organizations?.note}</p></Card>
              </div>
            </div>
          )}

          {sub === 'regions' && (
            <Card className="border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200"><tr>
                  {['Region', 'Admins', 'Employer Accts', 'Listings', 'Active Opps', 'Applications', 'Hires'].map(h => <th key={h} className="text-left p-3 text-xs font-semibold text-slate-900">{h}</th>)}
                </tr></thead>
                <tbody>
                  {(d.regions || []).map(r => (
                    <tr key={r.region_id} className="border-b border-slate-100">
                      <td className="p-3 text-slate-900 font-medium">{r.name}<span className="text-slate-400 text-xs"> {r.state}</span></td>
                      <td className="p-3">{r.regional_admins}</td><td className="p-3">{r.employer_accounts}</td>
                      <td className="p-3">{r.listing_organizations}</td><td className="p-3">{r.active_opportunities}</td>
                      <td className="p-3">{r.applications}</td><td className="p-3">{r.hires}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-amber-600 p-3">{d.limitation}</p>
            </Card>
          )}

          {sub === 'opportunities' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label="Active Opportunities" value={d.inventory?.active} />
                <Metric label="SkillBridge" value={d.inventory?.skillbridge} />
                <Metric label="Employment" value={d.inventory?.employment} />
                <Metric label="Saved" value={d.inventory?.saved} />
                <Metric label="Views" value={d.since_tracking_began?.opportunity_views} sub="since tracking began" />
                <Metric label="Searches" value={d.since_tracking_began?.opportunity_searches} sub="since tracking began" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-2">High view · low application <span className="text-xs font-normal text-slate-400">(since tracking began)</span></h4>
                <Card className="border border-slate-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b"><tr><th className="text-left p-3 text-xs font-semibold">Opportunity</th><th className="p-3 text-xs font-semibold">Views</th><th className="p-3 text-xs font-semibold">Applications</th></tr></thead>
                    <tbody>
                      {(d.high_view_low_application || []).map(o => (
                        <tr key={o.job_id} className="border-b border-slate-100"><td className="p-3 text-slate-900">{o.title || o.job_id}</td><td className="p-3 text-center">{o.views}</td><td className="p-3 text-center">{o.applications}</td></tr>
                      ))}
                      {(!d.high_view_low_application || d.high_view_low_application.length === 0) && <tr><td colSpan="3" className="p-4 text-center text-slate-400">No tracked views yet.</td></tr>}
                    </tbody>
                  </table>
                </Card>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
